import { ConstraintFragment, useProductGroupsQuery } from '@occc/graphql';
import { useConfig } from '@occc/config';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { useGetLocationsQuery } from '@occc-app/dataAccessLayer/location/queries';
import { useGetLocationGroupsByUuids } from '@occc-app/dataAccessLayer/locationGroup/queries';
import { getProductGroupIdsToLoad } from '@occc-app/dataAccessLayer/productGroup/utils';

import { useMemo } from 'react';
import { buildConstraintSupplementalData } from './build-constraint-supplemental-data';
import { ConstraintSupplementalData } from './component-details.types';
import { useGetAudienceSelectItemsByUuids } from '../../../dataAccessLayer/audience/useGetAudienceSelectItemsByUuids';

type UseConstraintSupplementalDataParams = {
  constraints: ConstraintFragment[];
  campaignId: string;
  chainId: string | undefined;
  isComponentFromDifferentChain: boolean;
};

export const useConstraintSupplementalData = ({
  constraints,
  campaignId,
  chainId,
  isComponentFromDifferentChain,
}: UseConstraintSupplementalDataParams) => {
  const { config } = useConfig();

  const audienceIdsForConstraints = useMemo(() => {
    const ids = constraints
      .filter(constraintHelper.isInAudience)
      .map((constraint) => constraint.audienceId)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [constraints]);

  const { data: audiencesForConstraints, loading: audiencesForConstraintsLoading } =
    useGetAudienceSelectItemsByUuids({
      uuids: audienceIdsForConstraints,
      chainId: chainId,
      skip: isComponentFromDifferentChain,
    });

  const productGroupIdsToLoad = getProductGroupIdsToLoad(constraints);

  const { data: productGroupsResponse, loading: productGroupsLoading } = useProductGroupsQuery({
    variables: {
      filter: {
        idContains: productGroupIdsToLoad,
      },
    },
    skip: productGroupIdsToLoad.length === 0 || isComponentFromDifferentChain,
  });

  const productGroups = productGroupsResponse?.productGroups.edges.map((edge) => edge.node);

  const storeLocationIds = useMemo(() => {
    const ids = constraints
      .filter(constraintHelper.isStoreLocation)
      .flatMap((constraint) => constraint.locations)
      .map((location) => location.id)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [constraints]);

  const { data: storeLocationsData, loading: storeLocationsLoading } = useGetLocationsQuery({
    skip: storeLocationIds.length === 0 || isComponentFromDifferentChain,
    variables: {
      filter: { uuidContains: storeLocationIds },
    },
  });

  const storeLocationGroupUuids = useMemo(() => {
    const uuids = constraints
      .filter(constraintHelper.isStoreLocationGroup)
      .flatMap((constraint) => constraint.locationGroups)
      .map((locationGroup) => locationGroup.uuid)
      .filter(Boolean);
    return [...new Set(uuids)];
  }, [constraints]);

  const { data: storeLocationGroupsData, loading: storeLocationGroupsLoading } =
    useGetLocationGroupsByUuids(storeLocationGroupUuids, isComponentFromDifferentChain);

  const voucheredOffersData = useMemo(() => {
    const offers = constraints
      .filter(constraintHelper.isPurchasableEligibility)
      .flatMap((constraint) => constraint.voucheredOffers);

    return [
      ...new Map(offers.map((offer) => [offer.id, { id: offer.id, name: offer.name }])).values(),
    ];
  }, [constraints]);

  const subBrandUuidsForConstraints = useMemo(() => {
    const uuids = constraints
      .filter(constraintHelper.isSubBrand)
      .flatMap((constraint) => constraint.subBrandUuids);
    return [...new Set(uuids)];
  }, [constraints]);

  const subBrandsForConstraints = useMemo(() => {
    const list = config?.subBrands?.enabled ? config.subBrands?.list ?? [] : [];
    return subBrandUuidsForConstraints
      .map((uuid) => list.find((sb) => sb.uuid === uuid))
      .filter((subBrand): subBrand is NonNullable<typeof subBrand> => !!subBrand)
      .map((subBrand) => ({ uuid: subBrand.uuid, name: subBrand.name }));
  }, [config?.subBrands, subBrandUuidsForConstraints]);

  const constraintSupplementalData: ConstraintSupplementalData = useMemo(
    () =>
      buildConstraintSupplementalData({
        campaignId,
        audiencesForConstraints,
        productGroups,
        storeLocationsData,
        storeLocationGroupsData,
        voucheredOffersData,
        subBrandsForConstraints,
      }),
    [
      campaignId,
      audiencesForConstraints,
      productGroups,
      storeLocationsData,
      storeLocationGroupsData,
      voucheredOffersData,
      subBrandsForConstraints,
    ],
  );

  const loading = useMemo(
    () =>
      audiencesForConstraintsLoading ||
      productGroupsLoading ||
      storeLocationsLoading ||
      storeLocationGroupsLoading,
    [
      audiencesForConstraintsLoading,
      productGroupsLoading,
      storeLocationsLoading,
      storeLocationGroupsLoading,
    ],
  );

  const initiallyLoading = useMemo(() => {
    const notSkipping = !isComponentFromDifferentChain;
    return (
      (audienceIdsForConstraints.length > 0 && notSkipping && !audiencesForConstraints) ||
      (productGroupIdsToLoad.length > 0 &&
        notSkipping &&
        (productGroupsLoading || !productGroupsResponse)) ||
      (storeLocationIds.length > 0 && notSkipping && !storeLocationsData) ||
      (storeLocationGroupUuids.length > 0 && notSkipping && !storeLocationGroupsData)
    );
  }, [
    isComponentFromDifferentChain,
    audienceIdsForConstraints.length,
    audiencesForConstraints,
    productGroupIdsToLoad.length,
    productGroupsLoading,
    productGroupsResponse,
    storeLocationIds.length,
    storeLocationsData,
    storeLocationGroupUuids.length,
    storeLocationGroupsData,
  ]);

  return useMemo(
    () => ({
      constraintSupplementalData,
      productGroups,
      loading,
      initiallyLoading,
    }),
    [constraintSupplementalData, productGroups, loading, initiallyLoading],
  );
};
